import { Component } from '@theme/component';
import { prefersReducedMotion, preloadImage } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const SWIPE_THRESHOLD = 46;
const SWIPE_MIN_DISTANCE = 22;
const SWIPE_VELOCITY = 0.42;
const SWIPE_DOMINANCE = 1.16;
const TRANSITION_MS = 220;
const CLICK_SUPPRESSION_MS = 460;
const WHEEL_THRESHOLD = 36;
const WHEEL_IDLE_MS = 190;
const DISMISS_THRESHOLD = 72;
const DISMISS_MIN_DISTANCE = 42;
const DISMISS_VELOCITY = 0.5;
const DISMISS_DOMINANCE = 1.18;
const DISMISS_MAX_DRAG = 280;
const DISMISS_ANIMATION_MS = 165;
const PROGRESS_EDGE_INSET = 3.5;
const CONTROL_SELECTOR =
  '[data-aw-control], .aw-zoom-dialog__nav, .aw-zoom-dialog__reset, .dialog-zoomed-gallery__close-button, .dialog-thumbnails-list, model-viewer';

/**
 * Product media dialog with one cyclic media index for images, videos and
 * models. Images remain draggable only above 1x; at 1x the same gesture moves
 * exactly one media item.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 * @property {HTMLElement} gallery
 * @property {HTMLButtonElement} [closeButton]
 * @property {HTMLButtonElement} [resetZoom]
 * @property {HTMLElement} [current]
 * @property {HTMLElement} [progress]
 * @property {HTMLElement} [liveRegion]
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails', 'gallery'];

  /** @type {Set<string>} */
  #highResImagesLoaded = new Set();

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number} */
  #activeIndex = 0;

  /** @type {boolean} */
  #transitionLocked = false;

  /** @type {number | null} */
  #transitionTimer = null;

  /** @type {number} */
  #transitionGeneration = 0;

  /** @type {HTMLElement | null} */
  #returnFocus = null;

  /** @type {{id:number,x:number,y:number,lastX:number,lastY:number,time:number,moved:boolean,axis:'pending'|'horizontal'|'dismiss'|'vertical',fromZoomWrapper:boolean} | null} */
  #pointer = null;

  /** @type {number | null} */
  #dismissTimer = null;

  /** @type {number} */
  #suppressClickUntil = 0;

  /** @type {number} */
  #wheelTotal = 0;

  /** @type {boolean} */
  #wheelLatched = false;

  /** @type {number | null} */
  #wheelTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog, gallery } = this.refs;

    dialog.addEventListener('antlitzwerk:zoom-swipe', this.handleImageSwipe, { signal });
    dialog.addEventListener('antlitzwerk:zoom-change', this.#handleZoomChange, { signal });
    dialog.addEventListener('wheel', this.#handleWheel, { passive: false, signal });
    dialog.addEventListener('cancel', this.#handleCancel, { signal });
    dialog.addEventListener('click', this.#handleClickCapture, { capture: true, signal });

    gallery.addEventListener('pointerdown', this.#handlePointerDown, { capture: true, signal });
    gallery.addEventListener('pointermove', this.#handlePointerMove, { capture: true, passive: false, signal });
    gallery.addEventListener('pointerup', this.#handlePointerEnd, { capture: true, signal });
    gallery.addEventListener('pointercancel', this.#handlePointerEnd, { capture: true, signal });

    this.#applySelection(this.#selectedIndex(), {
      instant: true,
      announce: false,
      dispatch: false,
      direction: 0,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#clearTransitionTimer();
    this.#clearWheelTimer();
    this.#clearDismissTimer();
  }

  /** The product gallery uses this to suppress a video click generated after a swipe. */
  isClickSuppressed() {
    return performance.now() < this.#suppressClickUntil;
  }

  /**
   * Opens the exact source index from the normal gallery.
   * @param {number} index
   * @param {Event} event
   */
  async open(index, event) {
    event?.preventDefault?.();

    const total = this.refs.media.length;
    if (!total) return;

    const normalized = this.#normalize(index);
    const eventTarget = event?.target instanceof Element ? event.target : null;
    this.#returnFocus = /** @type {HTMLElement | null} */ (
      eventTarget?.closest('button, [role="button"], [tabindex]') ||
      eventTarget?.closest('[data-aw-media-index]') ||
      this.closest('media-gallery')
    );

    this.#resetMediaZoom();
    this.#resetDismissGesture(false);
    this.#clearTransitionTimer();
    this.#transitionGeneration += 1;
    this.#transitionLocked = false;

    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.refs.dialog.classList.remove('dialog--closed');
    this.dataset.open = 'true';

    this.#dispatchLifecycle('antlitzwerk:zoom-open', normalized);
    await this.#applySelection(normalized, {
      instant: true,
      announce: false,
      dispatch: true,
      direction: 0,
    });

    queueMicrotask(() => {
      const focusTarget = this.refs.closeButton || this.refs.dialog;
      focusTarget?.focus?.({ preventScroll: true });
    });
  }

  /** Closes the modal and returns focus to the exact opening control/media. */
  close(event) {
    event?.preventDefault?.();
    if (!this.refs.dialog.open) return;

    this.#dispatchLifecycle('antlitzwerk:zoom-closing', this.#activeIndex);
    this.refs.dialog.classList.add('dialog--closed');
    this.#pointer = null;
    this.#resetMediaZoom();
    this.#resetDismissGesture(false);
    this.#clearTransitionTimer();
    this.#transitionGeneration += 1;
    this.#transitionLocked = false;

    this.refs.dialog.close();
    delete this.dataset.open;
    window.dispatchEvent(new DialogCloseEvent());
    this.#dispatchLifecycle('antlitzwerk:zoom-close', this.#activeIndex);

    const focusTarget = this.#returnFocus;
    this.#returnFocus = null;
    requestAnimationFrame(() => {
      this.refs.dialog.classList.remove('dialog--closed');
      if (focusTarget?.isConnected) focusTarget.focus?.({ preventScroll: true });
    });
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.close(event);
  };

  #dispatchLifecycle(name, index) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail: {
          index: this.#sourceIndex(index),
          dialogIndex: index,
        },
      })
    );
  }

  /** @param {number} value */
  #normalize(value) {
    const total = this.refs.media.length;
    if (!total) return 0;
    return ((Number(value) || 0) % total + total) % total;
  }

  #selectedIndex() {
    const selected = Array.from(this.refs.thumbnails?.querySelectorAll?.('button') || []).findIndex(
      (button) => button.getAttribute('aria-selected') === 'true'
    );
    return selected >= 0 ? selected : this.#activeIndex;
  }

  /** @param {number} index */
  #sourceIndex(index) {
    const mapped = Number(this.refs.media[index]?.dataset?.awSourceIndex);
    return Number.isInteger(mapped) && mapped >= 0 ? mapped : index;
  }

  /**
   * Horizon's infinite slideshow temporarily reorders DOM nodes at the loop
   * boundary. Its native zoom-sync event expects the target's current
   * slideshow-array position, while ANTLITZWERK lifecycle events keep using the
   * stable source index. Mapping here prevents an edge transition from syncing
   * the inline gallery to an adjacent medium.
   * @param {number} sourceIndex
   */
  #slideshowIndexForSource(sourceIndex) {
    const mediaGallery = this.closest('media-gallery');
    const slideshow = mediaGallery?.slideshow || mediaGallery?.querySelector?.('slideshow-component');
    const target = slideshow?.querySelector?.(
      `slideshow-slide[data-aw-media-index="${Number(sourceIndex) || 0}"]`
    );
    const slides = Array.from(slideshow?.slides || slideshow?.refs?.slides || []);
    const currentIndex = target ? slides.indexOf(target) : -1;
    return currentIndex >= 0 ? currentIndex : sourceIndex;
  }

  #activeZoomWrapper() {
    return this.refs.media[this.#activeIndex]?.querySelector?.('drag-zoom-wrapper') || null;
  }

  #activeImageIsZoomed() {
    return this.#activeZoomWrapper()?.dataset.zoomActive === 'true';
  }

  /** @param {number | null} [index] */
  #resetMediaZoom(index = null) {
    const wrappers = index == null
      ? this.refs.dialog.querySelectorAll('drag-zoom-wrapper')
      : [this.refs.media[index]?.querySelector?.('drag-zoom-wrapper')].filter(Boolean);

    for (const wrapper of wrappers) {
      if (typeof wrapper?.resetZoom === 'function') wrapper.resetZoom();
    }
    this.#updateResetControl();
  }

  resetActiveZoom(event) {
    event?.preventDefault?.();
    const activeMedia = this.refs.media[this.#activeIndex];
    this.#resetMediaZoom(this.#activeIndex);
    if (activeMedia instanceof HTMLElement) {
      activeMedia.tabIndex = -1;
      activeMedia.focus({ preventScroll: true });
    }
  }

  #handleZoomChange = (event) => {
    const sourceMedia = event.target instanceof Element
      ? event.target.closest('.product-media-container')
      : null;
    if (sourceMedia !== this.refs.media[this.#activeIndex]) return;
    this.#updateResetControl();
  };

  #updateResetControl() {
    if (!(this.refs.resetZoom instanceof HTMLButtonElement)) return;
    this.refs.resetZoom.hidden = !this.#activeImageIsZoomed();
  }

  /**
   * Loads one high-resolution image only when it becomes relevant.
   * @param {HTMLElement} mediaContainer
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer?.classList?.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);
    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.decoding = 'async';
    newImage.draggable = false;
    newImage.style.cssText = image.style.cssText;
    newImage.loading = 'eager';
    newImage.fetchPriority = 'high';
    for (const attribute of ['width', 'height', 'aria-hidden']) {
      const value = image.getAttribute(attribute);
      if (value != null) newImage.setAttribute(attribute, value);
    }
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      if (!image.isConnected) return;
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    newImage.src = highResolutionUrl;
    return true;
  }

  /** @param {number} index */
  async handleThumbnailPointerEnter(index) {
    const media = this.refs.media[this.#normalize(index)];
    if (media) this.loadHighResolutionImage(media);
  }

  /** @param {number} index @param {Event} [event] */
  async handleThumbnailClick(index, event) {
    event?.preventDefault?.();
    if (this.#transitionLocked) return;
    const target = this.#normalize(index);
    const direction = target === this.#activeIndex ? 0 : target > this.#activeIndex ? 1 : -1;
    await this.#applySelection(target, {
      instant: prefersReducedMotion(),
      announce: true,
      dispatch: true,
      direction,
    });
  }

  /** @param {Event} [event] */
  previous(event) {
    event?.preventDefault?.();
    this.#navigate(-1, true);
  }

  /** @param {Event} [event] */
  next(event) {
    event?.preventDefault?.();
    this.#navigate(1, true);
  }

  /** @param {-1 | 1} direction @param {boolean} userInitiated */
  #navigate(direction, userInitiated) {
    if (this.#transitionLocked || this.refs.media.length <= 1 || this.#activeImageIsZoomed()) return;
    void this.#applySelection(this.#activeIndex + direction, {
      instant: prefersReducedMotion(),
      announce: userInitiated,
      dispatch: true,
      direction,
    });
  }

  /**
   * @param {number} index
   * @param {{instant:boolean,announce:boolean,dispatch:boolean,direction:number}} options
   */
  async #applySelection(index, options) {
    const media = this.refs.media;
    if (!media.length) return;

    const targetIndex = this.#normalize(index);
    const previousIndex = this.#activeIndex;
    const previous = media[previousIndex];
    const target = media[targetIndex];
    if (!(target instanceof HTMLElement)) return;

    this.#resetMediaZoom(previousIndex);
    this.#resetMediaZoom(targetIndex);
    this.#clearTransitionTimer();
    const transitionGeneration = ++this.#transitionGeneration;

    const changed = targetIndex !== previousIndex;
    const instant = options.instant || !changed;
    const direction = options.direction || (targetIndex > previousIndex ? 1 : -1);

    for (let i = 0; i < media.length; i += 1) {
      const item = media[i];
      if (!(item instanceof HTMLElement)) continue;
      item.classList.remove(
        'is-aw-entering',
        'is-aw-entering--next',
        'is-aw-entering--previous',
        'is-aw-leaving',
        'is-aw-leaving--next',
        'is-aw-leaving--previous'
      );
      if (item === previous || item === target) continue;
      item.classList.remove('is-aw-active');
      item.setAttribute('aria-hidden', 'true');
      item.setAttribute('inert', '');
    }

    target.removeAttribute('inert');
    target.setAttribute('aria-hidden', 'false');

    if (changed && previous instanceof HTMLElement) {
      previous.setAttribute('aria-hidden', 'true');
      previous.setAttribute('inert', '');

      if (instant) {
        previous.classList.remove('is-aw-active');
        target.classList.add('is-aw-active');
      } else {
        this.#transitionLocked = true;
        target.classList.remove('is-aw-active');
        previous.classList.add('is-aw-leaving', direction > 0 ? 'is-aw-leaving--next' : 'is-aw-leaving--previous');
        previous.classList.remove('is-aw-active');
        target.classList.add('is-aw-entering', direction > 0 ? 'is-aw-entering--next' : 'is-aw-entering--previous');

        // Separate the initial and final visual states without layout reads.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (transitionGeneration === this.#transitionGeneration && this.refs.dialog.open) {
              target.classList.add('is-aw-active');
            }
          });
        });

        const duration = prefersReducedMotion() ? 0 : TRANSITION_MS;
        this.#transitionTimer = window.setTimeout(() => {
          if (transitionGeneration !== this.#transitionGeneration) return;
          previous.classList.remove('is-aw-leaving', 'is-aw-leaving--next', 'is-aw-leaving--previous');
          target.classList.remove('is-aw-entering', 'is-aw-entering--next', 'is-aw-entering--previous');
          target.classList.add('is-aw-active');
          this.#transitionLocked = false;
          this.#transitionTimer = null;
        }, duration + 40);
      }
    } else {
      target.classList.add('is-aw-active');
    }

    this.#activeIndex = targetIndex;
    this.#updateThumbnails(targetIndex, options.instant ? 'instant' : 'smooth');
    this.#updateStatus(targetIndex, options.announce);
    this.#updateResetControl();
    if (this.refs.dialog.open) this.loadHighResolutionImage(target);

    if (options.dispatch) {
      const sourceIndex = this.#sourceIndex(targetIndex);
      this.dispatchEvent(new ZoomMediaSelectedEvent(this.#slideshowIndexForSource(sourceIndex)));
      this.#dispatchLifecycle('antlitzwerk:zoom-selected', targetIndex);
    }
  }

  /** @param {number} index @param {'instant' | 'smooth'} behavior */
  #updateThumbnails(index, behavior) {
    const { thumbnails } = this.refs;
    const buttons = Array.from(thumbnails.querySelectorAll('button'));
    for (let i = 0; i < buttons.length; i += 1) {
      buttons[i].setAttribute('aria-selected', `${i === index}`);
    }

    const target = buttons[index];
    if (!(target instanceof HTMLElement)) return;
    scrollIntoView(target, {
      ancestor: thumbnails,
      behavior: prefersReducedMotion() ? 'instant' : behavior,
      block: 'center',
      inline: 'center',
    });
  }

  /** @param {number} index @param {boolean} announce */
  #updateStatus(index, announce) {
    const total = this.refs.media.length;
    if (this.refs.progress instanceof HTMLElement) {
      const ratio = total > 1 ? index / (total - 1) : 0.5;
      const mappedPosition = PROGRESS_EDGE_INSET + ratio * (100 - PROGRESS_EDGE_INSET * 2);
      this.refs.progress.style.setProperty('--aw-progress-position', `${mappedPosition}`);
    }

    if (announce && this.refs.liveRegion) {
      const template = this.dataset.awStatusTemplate || 'Media __CURRENT__ of __TOTAL__';
      this.refs.liveRegion.textContent = template
        .replace('__CURRENT__', String(index + 1))
        .replace('__TOTAL__', String(total));
    }
  }

  handleImageSwipe = (event) => {
    const direction = event.detail?.direction;
    if (direction !== 'next' && direction !== 'previous') return;

    event.preventDefault();
    event.stopPropagation();
    this.#suppressClickUntil = performance.now() + CLICK_SUPPRESSION_MS;
    this.#navigate(direction === 'next' ? 1 : -1, true);
  };

  #pointerTargetAllowed(target) {
    if (!(target instanceof Element)) return false;
    if (target.closest(CONTROL_SELECTOR)) return false;
    if (this.#transitionLocked || this.classList.contains('is-aw-dismiss-committing')) return false;
    if (this.#activeImageIsZoomed()) return false;
    const activeMedia = this.refs.media[this.#activeIndex];
    return Boolean(activeMedia?.contains(target));
  }

  #handlePointerDown = (event) => {
    if (!event.isPrimary || event.button !== 0 || !this.#pointerTargetAllowed(event.target)) return;
    const target = event.target instanceof Element ? event.target : null;
    this.#clearDismissTimer();
    this.#pointer = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      time: performance.now(),
      moved: false,
      axis: 'pending',
      fromZoomWrapper: Boolean(target?.closest('drag-zoom-wrapper')),
    };
  };

  #handlePointerMove = (event) => {
    const pointer = this.#pointer;
    if (!pointer || pointer.id !== event.pointerId) return;

    pointer.lastX = event.clientX;
    pointer.lastY = event.clientY;
    const dx = pointer.lastX - pointer.x;
    const dy = pointer.lastY - pointer.y;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    const distance = Math.hypot(dx, dy);

    if (distance > 9) pointer.moved = true;
    if (pointer.axis === 'pending' && distance > 9) {
      if (absX > absY * SWIPE_DOMINANCE) pointer.axis = 'horizontal';
      else if (dy > 0 && absY > absX * DISMISS_DOMINANCE) pointer.axis = 'dismiss';
      else if (dy < 0 && absY > absX) pointer.axis = 'vertical';
    }

    if (pointer.axis === 'dismiss') {
      if (event.cancelable) event.preventDefault();
      this.#updateDismissDrag(Math.max(0, dy));
      return;
    }

    if (pointer.axis === 'horizontal' && event.cancelable) event.preventDefault();
  };

  #handlePointerEnd = (event) => {
    const pointer = this.#pointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    this.#pointer = null;

    const cancelled = event.type === 'pointercancel';
    const endX = Number.isFinite(event.clientX) ? event.clientX : pointer.lastX;
    const endY = Number.isFinite(event.clientY) ? event.clientY : pointer.lastY;
    const dx = endX - pointer.x;
    const dy = endY - pointer.y;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    const elapsed = Math.max(1, performance.now() - pointer.time);
    const horizontalVelocity = absX / elapsed;
    const downwardVelocity = Math.max(0, dy) / elapsed;
    const horizontal = absX > absY * SWIPE_DOMINANCE;
    const dismissThreshold = Math.min(96, Math.max(DISMISS_THRESHOLD, window.innerHeight * 0.09));
    const validDismiss = !cancelled && pointer.axis === 'dismiss' && dy > 0 && absY > absX * DISMISS_DOMINANCE && (
      dy >= dismissThreshold ||
      (dy >= DISMISS_MIN_DISTANCE && downwardVelocity >= DISMISS_VELOCITY)
    );
    const validHorizontal = !cancelled && horizontal && (
      absX >= SWIPE_THRESHOLD ||
      (absX >= SWIPE_MIN_DISTANCE && horizontalVelocity >= SWIPE_VELOCITY)
    );

    if (pointer.moved) this.#suppressClickUntil = performance.now() + CLICK_SUPPRESSION_MS;

    if (validDismiss) {
      this.#commitDismiss(event);
      return;
    }

    if (pointer.axis === 'dismiss') this.#resetDismissGesture(true);
    if (validHorizontal && !pointer.fromZoomWrapper) this.#navigate(dx < 0 ? 1 : -1, true);
  };

  #updateDismissDrag(distance) {
    const viewportHeight = Math.max(1, window.innerHeight);
    const softCap = Math.min(DISMISS_MAX_DRAG, viewportHeight * 0.34);
    const translated = distance <= softCap
      ? distance
      : softCap + (distance - softCap) * 0.18;
    const progress = Math.min(1, distance / Math.max(150, viewportHeight * 0.28));

    this.classList.add('is-aw-dismiss-dragging');
    this.classList.remove('is-aw-dismiss-committing');
    this.style.setProperty('--aw-dismiss-y', `${translated}px`);
    this.style.setProperty('--aw-dismiss-opacity', `${1 - progress * 0.22}`);
  }

  #resetDismissGesture(animate = true) {
    this.#clearDismissTimer();
    this.classList.remove('is-aw-dismiss-dragging', 'is-aw-dismiss-committing');

    if (!animate || prefersReducedMotion()) {
      this.style.removeProperty('--aw-dismiss-y');
      this.style.removeProperty('--aw-dismiss-opacity');
      return;
    }

    this.style.setProperty('--aw-dismiss-y', '0px');
    this.style.setProperty('--aw-dismiss-opacity', '1');
    this.#dismissTimer = window.setTimeout(() => {
      this.#dismissTimer = null;
      this.style.removeProperty('--aw-dismiss-y');
      this.style.removeProperty('--aw-dismiss-opacity');
    }, 190);
  }

  #commitDismiss(event) {
    this.#clearDismissTimer();
    this.classList.remove('is-aw-dismiss-dragging');
    this.classList.add('is-aw-dismiss-committing');
    this.#suppressClickUntil = performance.now() + CLICK_SUPPRESSION_MS;

    const targetDistance = Math.min(360, Math.max(220, window.innerHeight * 0.38));
    this.style.setProperty('--aw-dismiss-y', `${targetDistance}px`);
    this.style.setProperty('--aw-dismiss-opacity', '0');

    const duration = prefersReducedMotion() ? 0 : DISMISS_ANIMATION_MS;
    this.#dismissTimer = window.setTimeout(() => {
      this.#dismissTimer = null;
      this.close(event);
    }, duration);
  }

  #handleClickCapture = (event) => {
    if (performance.now() >= this.#suppressClickUntil) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('[data-aw-product-video], .product-media-container--model')) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  #handleWheel = (event) => {
    if (this.refs.media.length <= 1 || this.#activeImageIsZoomed()) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest(CONTROL_SELECTOR)) return;

    const horizontalDelta = event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)
      ? event.deltaY
      : event.deltaX;
    const verticalDelta = event.shiftKey ? 0 : event.deltaY;
    if (Math.abs(horizontalDelta) <= Math.abs(verticalDelta) * 1.05) return;

    event.preventDefault();
    this.#clearWheelTimer();
    this.#wheelTimer = window.setTimeout(() => {
      this.#wheelTotal = 0;
      this.#wheelLatched = false;
      this.#wheelTimer = null;
    }, WHEEL_IDLE_MS);

    if (this.#transitionLocked) {
      this.#wheelLatched = true;
      return;
    }

    if (this.#wheelLatched) return;
    this.#wheelTotal += horizontalDelta;
    if (Math.abs(this.#wheelTotal) < WHEEL_THRESHOLD) return;

    this.#wheelLatched = true;
    this.#navigate(this.#wheelTotal > 0 ? 1 : -1, true);
  };

  /**
   * Escape closes everywhere. Arrow navigation is intentionally disabled while
   * the active image is above 1x or a native interactive element has focus.
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(event);
      return;
    }

    if (
      event.target instanceof Element &&
      event.target.closest('input, select, textarea, video, audio, model-viewer, [contenteditable="true"]')
    ) return;
    if (this.#activeImageIsZoomed()) return;
    if (this.#transitionLocked) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.#navigate(-1, true);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.#navigate(1, true);
        break;
      case 'Home':
        event.preventDefault();
        void this.#applySelection(0, {
          instant: prefersReducedMotion(),
          announce: true,
          dispatch: true,
          direction: -1,
        });
        break;
      case 'End':
        event.preventDefault();
        void this.#applySelection(this.refs.media.length - 1, {
          instant: prefersReducedMotion(),
          announce: true,
          dispatch: true,
          direction: 1,
        });
        break;
      default:
        break;
    }
  }

  #clearTransitionTimer() {
    if (this.#transitionTimer != null) window.clearTimeout(this.#transitionTimer);
    this.#transitionTimer = null;
  }

  #clearWheelTimer() {
    if (this.#wheelTimer != null) window.clearTimeout(this.#wheelTimer);
    this.#wheelTimer = null;
  }

  #clearDismissTimer() {
    if (this.#dismissTimer != null) window.clearTimeout(this.#dismissTimer);
    this.#dismissTimer = null;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
